import type { MouseEvent } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { BilateralTradeProductType } from '@ebalance/proto/bfftypes/v1/bilateral_trade_product_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { TradingContractStatus } from '@ebalance/proto/bfftypes/v1/trading_contract_status_pb'
import type { TradingCustomDayCategory } from '@ebalance/proto/bfftypes/v1/trading_custom_day_category_pb'
import { TradingDeliveryType } from '@ebalance/proto/bfftypes/v1/trading_delivery_type_pb'
import { TradingExecutionRoute } from '@ebalance/proto/bfftypes/v1/trading_execution_route_pb'
import type { TradingTimeZoneType } from '@ebalance/proto/bfftypes/v1/trading_time_zone_type_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  LightModeSemanticColors,
  TertiaryButton,
  Tooltip,
  chakra,
  fontSizes,
} from '@enechain/ecloud-designsystem'
import { FaTableRegular } from '@enechain/ecloud-icons'

import { DetailIconButton } from '~/components/common/MasterDataTable/RowActionButtons'
import {
  ROW_ACTION_GAP,
  type StickyShadowState,
  stickyActionCellStyle,
} from '~/components/common/MasterDataTable/stickyActionColumn'
import { SquareIconButton } from '~/components/common/SquareIconButton'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { iconSize } from '~/constants/iconSize'
// 「オプション」「実績利用率」「利用率(下限-上限)」列は本画面のスコープ外
// (通告変更管理 P3 へ移管。 spec bilateral-trade-recap.md AC-SBR-008〜010 欠番)。
import {
  AreaCodeLabel,
  DeliveryTypeBadge,
  ExecutionRouteBadge,
  LegTimeZoneLabel,
  ProductTypeBadge,
  StatusBadge,
} from '~/pages/SupplyBilateral/components/Badges'
import {
  formatExecutionDate,
  formatPartnerWithCode,
  formatPeriodRange,
} from '~/pages/SupplyBilateral/models/formatters'

// 後方互換用 type alias (= 既存テストが参照)
export type TradeRowBuySell = 'buy' | 'sell'

type Props = {
  id: string
  publicTradeId: string
  executionDate?: ProtoDate | { year: number; month: number; day: number }
  /** 約定経路 (enechain / eSquareLive / ベースロード市場 / 先渡し市場 / 常時BU / その他 / 手入力) */
  executionRoute: TradingExecutionRoute
  /** 商品形態 (標準 / スプレッド / 非定型)。backend が保存状態から導出する。 */
  productType: BilateralTradeProductType
  /** ライフサイクル状態 (成立済 / ドラフト / 期間満了 / 取消) */
  status: TradingContractStatus
  /** 相対先名 */
  partnerName: string
  /** 取引先コード (取引先 BG / 計画提出者コード)。 未登録は空文字で、 名称のみ表示する。 */
  partnerCode: string
  buySell: TradeRowBuySell
  /** エリアコード */
  areaCode: AreaCode
  /** 受渡期間 From */
  deliveryPeriodFrom?: ProtoDate | { year: number; month: number; day: number }
  /** 受渡期間 To */
  deliveryPeriodTo?: ProtoDate | { year: number; month: number; day: number }
  /** 受渡区分 (enum 固定値)。 旧 master id (UUID) から enum に変更 (delivery-type-enum-spec §6)。 */
  deliveryType: TradingDeliveryType
  /**
   * 時間帯区分。契約の永続値そのまま (issues #7762 / #7780 / #7782): 名前付き
   * (ベース / ピーク / オフピーク) はその enum、時間帯 enum を持たない
   * 契約は UNSPECIFIED。UNSPECIFIED の場合は customBands の 日区分＋時刻レンジ
   * を、customBands も空なら「—」を表示する (LegTimeZoneLabel)。
   */
  timeZoneType: TradingTimeZoneType
  /** カスタム時間帯の 日区分 ↔ 時刻レンジ ペア列 (timeZoneType=UNSPECIFIED の時のみ参照)。 */
  customBands?: Array<{
    dayCategories: TradingCustomDayCategory[]
    timeSlots: Array<{ start: string; end: string }>
  }>
  /** オプション有無。 TODO(#4787): P0 は列ごと非表示 (P1 の通告変更管理で復活)。 */
  hasOption: boolean
  /** 実績利用率 (0-1 スケール)。 TODO(#4787): P0 は列ごと非表示。 */
  actualUtilizationRate?: number
  /** 利用率下限 (0-1 スケール)。 TODO(#4787): P0 は列ごと非表示。 */
  utilizationRateMin?: number
  /** 利用率上限 (0-1 スケール)。 TODO(#4787): P0 は列ごと非表示。 */
  utilizationRateMax?: number
  /** 数量 (MW) */
  volumeMw: string
  /** 契約単価 (円/kWh)。 leg 次第で unset 可 (= undefined なら空表示)。 */
  unitPriceYenPerKwh?: number
  isManual: boolean
  /**
   * ロール識別子。 権限制御に使用。
   * TODO(#4801): P0 は編集・削除ボタン自体が非表示のため未使用 (P1 で復活)。
   */
  userRole?: string
  /**
   * workflow-back-bar-readonly AC-19: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき削除ボタンを disabled にする（表示切替系ではなく編集・確定操作のため）。
   * TODO(#4801): P0 は削除ボタン自体が非表示のため未使用 (P1 で復活)。
   */
  isReadonly?: boolean
  /** Recap ボタンクリック時のコールバック */
  onRecap?: (id: string) => void
  /** 詳細ボタンクリック時のコールバック (手入力取引閲覧モーダル / Feature D)。 */
  onViewDetail?: (id: string) => void
  /** 編集ボタンクリック時のコールバック。 TODO(#4801): P0 は編集ボタン非表示。 */
  onEdit?: (id: string) => void
  /** 削除ボタンクリック時のコールバック。 TODO(#4801): P0 は削除ボタン非表示。 */
  onDelete?: (id: string) => void
  /** 「48点データへ」ボタンクリック時のコールバック (48点データ画面へ遷移)。 */
  onGoTo48?: (id: string, event?: MouseEvent) => void
  /**
   * 0 始まりの行 index。 zebra (design `.ecl-table tbody tr:nth-child(even)`) と、
   * 右端固定の操作列に同じ zebra を塗るために使う (sticky cell は tr の背景を
   * 継承できないため)。 単体描画時は 0 (= 偶数行) 扱い。
   */
  rowIndex?: number
  /**
   * 操作列の影の状態。 親 (`TradeTable`) が `useStickyRightShadow` で判定して渡す
   * (横 overflow 無し = `off` / 右端まで scroll 済 = `end` のときは陰を出さない)。
   */
  stickyShadow?: StickyShadowState
}

/**
 * 取引一覧テーブルの 1 行コンポーネント (14 列構成、 design/supply/bilateral.html 準拠)。
 *
 * 列構成:
 *   約定経路 / 商品形態 / 約定日 / ステータス / 取引ID / 相対先 / 売買 / エリア /
 *   受渡期間 / 受渡区分 / 時間帯 / 数量(MW) / 単価(円/kWh) / 操作
 *
 * P0 非表示 (#4787 / #4801、 P1 で復活):
 *   - 「オプション」「実績利用率」「利用率(下限-上限)」の 3 列 — P0 では該当データを
 *     保持しないため。 spec `bilateral-trade-list.md` Discussion 決定「オプション有無・
 *     利用率・通告タイミングの表示は通告変更管理画面に集約し本画面から撤去」
 *   - 「編集」「削除」ボタン — spec L453 決定事項 (Phase 1 は CSV 一括アップロードのみ) /
 *     L468 読み取り専用「取引一覧テーブルの全行で編集・削除ボタンが表示されないこと」。
 *     props (`onEdit` / `onDelete` / `userRole` / `isReadonly`) は P1 の再有効化に備えて
 *     型として残置している (= 呼び出し元の配線を壊さない)。
 *
 * 視覚装飾:
 *   - 売買 / 約定経路 / 受渡区分 / ステータス を色付きバッジで表示
 *   - 手入力行の識別は約定経路の「手入力」バッジのみ (= data-manual 属性は保持するが
 *     左境界の警告色マークは廃止。design 2026-06 改訂で撤去された)
 *
 * 操作ボタン (手入力 / 自動取得はボタンの有無で見分ける。 disabled では見分けさせない。 AC-SBL-010):
 *   - 48点データへ: 全行表示・全行有効。
 *   - Recap: 自動取得 (eSL 連携、 isManual=false) の行だけ。
 *   - 詳細: 手入力取引 (isManual=true) の行だけ (閲覧モーダル / Feature D)。
 *   全行に出るボタンを先 (左) に、 行の種類で有無が変わる Recap / 詳細を右端の 1 つの場所
 *   ([[SharedActionSlot]]) に置く。 空きがボタンとボタンの間に挟まらず列の右端に寄る
 *   (UI 辞書 components/table.md §1「実装時の注意」・screens/bilateral-trade.md 2c)。
 */
export const TradeRow = ({
  id,
  publicTradeId,
  executionDate,
  executionRoute,
  productType,
  status,
  partnerName,
  partnerCode,
  buySell,
  areaCode,
  deliveryPeriodFrom,
  deliveryPeriodTo,
  deliveryType,
  timeZoneType,
  customBands,
  volumeMw,
  unitPriceYenPerKwh,
  isManual,
  onRecap,
  onViewDetail,
  onGoTo48,
  rowIndex = 0,
  stickyShadow = 'off',
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const buySellEnum = buySell === 'buy' ? Side.BUY : Side.SELL
  const dateText =
    executionDate !== undefined ? formatExecutionDate(executionDate) : ''
  const periodText = formatPeriodRange(deliveryPeriodFrom, deliveryPeriodTo)

  const unitPriceText =
    unitPriceYenPerKwh === undefined ? '' : unitPriceYenPerKwh.toFixed(2)

  return (
    // zebra は design `.ecl-table tbody tr:nth-child(even)` 相当。 右端固定の操作列は
    // tr の背景を継承できず、 共通ロジック側が同じ zebra を cell に塗るため、 行本体にも
    // 同じ縞を出して 2 つの背景を一致させる。
    <chakra.tr
      // 偶数行は transparent でなく白を明示する。 かつては下のカード (Surface.primary)
      // が透けて白に見えていたが、 一覧の直置き化 (2026-08-31 デザイナー指摘①) で
      // カードが無くなり、 ページ地 (Surface.secondary) が透けて全行グレーに同化した
      // (同日デザイナー指摘「TD row に背景色がない」)。 右端固定列の塗りの既定
      // (--sticky-fill 未指定 = surface-primary) とも一致する。
      background={rowIndex % 2 === 1 ? 'Surface.secondary' : 'Surface.primary'}
      data-manual={isManual ? 'true' : undefined}
    >
      {/* [design 差異] ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
          design (`design/supply/bilateral.html`) は 3 列目 (約定日 の後ろ) だが、
          行の状態は他の属性を読む前に分かる必要があるため一覧の左端へ統一した。
          TradeTable の thead 側と対で並べ替えている。 */}
      <td style={cellStyleCenter}>
        <StatusBadge status={status} />
      </td>
      <td style={cellStyle}>
        <ExecutionRouteBadge route={executionRoute} />
      </td>
      <td style={cellStyleCenter}>
        <ProductTypeBadge productType={productType} />
      </td>
      <td style={cellStyle}>{dateText}</td>
      <td style={cellStylePublicTradeId}>{publicTradeId}</td>
      <td style={cellStylePartnerName}>
        {formatPartnerWithCode(partnerName, partnerCode)}
      </td>
      <td style={cellStyleCenter}>
        <TradeSideLabel side={buySellEnum} />
      </td>
      {/* エリア列は design 共通 JS (table-th-align.js centerLabeledColumns) が
          全画面で center 揃えに固定するため center を採用する。 */}
      <td style={cellStyleCenter}>
        <AreaCodeLabel areaCode={areaCode} />
      </td>
      <td style={cellStyle}>{periodText}</td>
      <td style={cellStyleCenter}>
        <DeliveryTypeBadge deliveryType={deliveryType} />
      </td>
      <td style={cellStyleCenter}>
        <LegTimeZoneLabel
          customBands={customBands}
          timeZoneType={timeZoneType}
        />
      </td>
      {/* TODO(#4801 / #4787): 「オプション」「実績利用率」「利用率(下限-上限)」の 3 セルは
          P0 では該当データを保持しないため非表示 (spec `bilateral-trade-list.md` の
          一覧列定義および Discussion 決定「オプション有無・利用率・通告タイミングの表示は
          通告変更管理画面に集約し本画面から撤去」)。 P1 (通告変更管理) で戻す際は
          `TradeTable` のヘッダ 3 列と対で復活させる (Badges 側の component は残置済み)。 */}
      <td style={cellStyleRight}>{volumeMw}</td>
      <td style={cellStyleRight}>{unitPriceText}</td>
      <td
        data-sticky-action
        // 影は globalCss の `::before` が描く (border-collapse 表では
        // td/th 直当ての box-shadow が paint されない。 issue #5489)。
        data-sticky-shadow={stickyShadow}
        style={{
          ...cellStyle,
          whiteSpace: 'nowrap',
          ...stickyActionCellStyle(rowIndex),
        }}
      >
        {/* ボタン間隔は全テーブル共通の 8px (design `.ecl-table td.cell-actions`
            の `gap: var(--space-2)`)。 td 自身に display:flex は当てない
            (border-collapse 表で行ズレを起こす design の既知 antipattern) ため、
            内側の HStack で並べる。 */}
        <HStack justifyContent="flex-end" gap={ROW_ACTION_GAP}>
          {/* 48点データへ ボタン: 全行表示・全行有効 (閲覧導線)。design/supply/bilateral.html
              の biz48Btn (取引所取引/eSL 含め全行で有効) 準拠。該当取引のコンテキストを
              クエリパラメータで引き渡しながら 48点データ画面へ遷移する (AC-010/AC-012)。 */}
          <Tooltip
            content={t('page:supplyBilateral.goTo48.tooltip')}
            placement="top"
            showArrow
          >
            <SquareIconButton
              aria-label={t('page:supplyBilateral.goTo48.tooltip')}
              onClick={(e) => onGoTo48?.(id, e)}
            >
              <Supply48Icon />
            </SquareIconButton>
          </Tooltip>

          {/* Recap ボタン: 自動取得 (eSL 連携) の行だけ。 手入力行は disabled で出さない。
              Recap と詳細は同じ行に並ばないので 1 つの場所を分け合う ([[SharedActionSlot]])。

              詳細ボタン: 手入力取引 (isManual=true) のみ表示。手入力取引閲覧モーダル
              (Feature D、read-only) を開く。eSL 連携取引は Recap で詳細を見るため非表示。

              テキストボタンではなく**アイコンボタン**にする (デザイナー指摘
              2026-08-27「取引契約一覧 > 詳細ボタンをアイコンボタンに (ワークフロー一覧
              同様)」)。 ワークフロー一覧 (`WorkflowCases/components/CaseTable.tsx`) と
              同じ共通部品を使う。

              アイコンは **eye** (`DetailIconButton`)。 2026-08-27 時点では
              `EditIconButton` (pen-to-square) を流用していたが、 2026-09-09
              デザイナー指定「全画面対象、 アイコンボタンで、 編集ではなく詳細の場合は
              `FaEyeRegular` を適用して編集導線と区別できるように」 で差し替えた。
              8080 (`design/supply/bilateral.html` の `bi-detail-btn`) はこの導線を
              「編集」 と呼び 詳細/編集 統合フォームを開くが、 **本アプリで開くのは
              read-only の閲覧モーダル** (Feature D。 編集 UI は P0 で未提供 =
              TODO(#4801)) なので、 いま画面上で起きることは閲覧であり eye が合う。
              P1 で編集できるようになった時点で pen-to-square へ戻す判断が要る。 */}
          {isManual ? (
            <SharedActionSlot
              reserved={
                <TertiaryButton size="md" tabIndex={-1}>
                  Recap
                </TertiaryButton>
              }
            >
              <DetailIconButton
                ariaLabel={t('common:action.detail')}
                onClick={() => onViewDetail?.(id)}
              />
            </SharedActionSlot>
          ) : (
            <SharedActionSlot
              reserved={
                <DetailIconButton
                  ariaLabel={t('common:action.detail')}
                  onClick={() => undefined}
                />
              }
            >
              <TertiaryButton
                width="100%"
                onClick={() => onRecap?.(id)}
                size="md"
              >
                Recap
              </TertiaryButton>
            </SharedActionSlot>
          )}

          {/* TODO(#4801 / #1574): 編集ボタン・削除ボタンは P0 では表示しない
              (spec `bilateral-trade-list.md` L453 決定事項「Phase 1 は CSV 一括
              アップロードのみ提供、 個別手動登録・編集・削除 UI は提供しない」/
              L468 読み取り専用「取引一覧テーブルの全行で編集・削除ボタンが表示されない
              こと」)。 P1 の再有効化に備え、 呼び出し元 (`TradeTable` → `SupplyBilateralPage`)
              の onEdit / onDelete 配線・削除確認ダイアログ・権限判定 (canMutate /
              deleteDisabled) は残置してあるので、 ここに 2 ボタンを戻せば復活する。
              P1 で削除ボタンを IconButton で戻す際は、有効時にも Tooltip でボタン名を
              出す (2026-08-17 デザイナー指摘)。 */}
        </HStack>
      </td>
    </chakra.tr>
  )
}

/**
 * 行の種類で出し分ける 2 つのボタン (自動取得の行 = Recap / 手入力の行 = 詳細) が分け合う 1 つの場所。
 * それぞれに場所を空けると、 どの行にも 1 つ分の空きが残る。
 *
 * 出さない方のボタン (`reserved`) を見えなくして同じマスに重ね、 場所の幅を 2 つの広い方にする。
 * 文字のボタンは呼び出し側が `width="100%"` でその幅いっぱいに広げる。 アイコンだけのボタンは
 * 正方形のまま左に寄せ、 余りは操作列の右端に出す (UI 辞書 components/table.md §1「実装時の注意」)。
 * visibility:hidden の要素はフォーカスも読み上げも受けない (念のため aria-hidden も付ける)。
 */
const SharedActionSlot = ({
  reserved,
  children,
}: {
  reserved: React.ReactElement
  children: React.ReactElement
}): React.ReactElement => (
  <Box display="grid">
    <Box gridArea="1 / 1" display="flex" visibility="hidden" aria-hidden="true">
      {reserved}
    </Box>
    <Box gridArea="1 / 1" display="flex">
      {children}
    </Box>
  </Box>
)

// TODO(#4801): P1 で削除ボタンを戻す時は、 design `.btn-outline-danger.btn-square` に合わせて
// borderColor Border.negativeMid / color Text.negativeMid / colorPalette red を当てる
// (共通部品 `MasterDataTable/RowActionButtons` の `DeleteIconButton` と同じ)。

// 48点データを開く導線は DS の表アイコンを 24px で置く (2026-10-20 デザイナー指定
// `<FaTableRegular boxSize="24px" />`)。 操作列の他の icon-only ボタン (iconSize.rowAction = 20px)
// より大きいのは意図的 (iconSize.supply48Link の docstring)。
const Supply48Icon = (): React.ReactElement => (
  <FaTableRegular boxSize={iconSize.supply48Link} aria-hidden="true" />
)

const cellStyle: React.CSSProperties = {
  // design (.ecl-table td) の padding: 6px 10px に揃える (§2.5 実測)。
  padding: '6px 10px',
  borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
  fontSize: fontSizes.md.value,
  verticalAlign: 'middle',
}

const cellStyleRight: React.CSSProperties = {
  ...cellStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
}

const cellStyleCenter: React.CSSProperties = {
  ...cellStyle,
  textAlign: 'center',
}

/**
 * 「取引ID」列 (デザイナー指摘 2026-08-13)。 詳細は
 * `TradeTable` の `headerStylePublicTradeId` コメント参照。 `whiteSpace: nowrap`
 * は table-layout:auto 環境で `-` 区切りの識別子が折り返されて幅指定
 * (`minWidth`) が無意味化しないための保険 (design/CLAUDE.md「Table」節: 基準幅は
 * th/td 両方に必要)。
 */
const cellStylePublicTradeId: React.CSSProperties = {
  ...cellStyle,
  whiteSpace: 'nowrap',
  minWidth: '20ch',
}

/**
 * 「相対先」列 (デザイナー指摘 2026-08-13: 可読性が悪いので幅を広げたい)。
 *
 * 8080 (`design/supply/bilateral.html`) はこの列に幅指定を持たず、 取引先名称
 * (`partnerName`) は実データにより長さが大きく変わる（`docs/specs/admin/master/
 * occto-master/trading-partner.md` の CounterpartyName は最大 100 文字）ため、
 * 固定 `minWidth` を決め打ちしない。 `whiteSpace: nowrap` だけを指定し、
 * table-layout:auto に列幅を実データの最長値へ自然に広げさせる (= 最小の
 * 広げ方。 他列を圧迫せず、 広がった分は横スクロールが吸収する)。
 *
 * 根本原因: 和文はどの文字の間でも改行しうる (CJK line-break) ため、 nowrap を
 * 指定しない td は table-layout:auto の「幅不足時は列を圧縮する」計算で
 * 1 文字幅まで潰され、 セル内で複数行に折り返されていた (= 今回の
 * 「可読性が悪い」の直接原因)。
 */
const cellStylePartnerName: React.CSSProperties = {
  ...cellStyle,
  whiteSpace: 'nowrap',
}
